import React, { useEffect, useState } from "react";
import {FormControl, InputLabel, MenuItem, Select, CircularProgress} from '@mui/material';
import { useSelector, useDispatch } from "react-redux";
import RootState from "../../../redux/states/root.state";
import { fetchAvailableFirmware, fetchAvailableDevFirmware, fetchAvailableAdminFirmware } from "../../../redux/actions/firmware.actions";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import { AppDispatch } from "../../../redux/store";
import { palName } from "../../../utils/brand";
import ModalShell, { ModalSpaList } from "./ModalShell";

interface ModalProps {
  isOpen: boolean,
  setOpen: any,
  setOpenSingle?: any,
  setOpenMultiple?: any,
  setFirmware: any,
  setVersion: any,
  spas: any[],
}

const ConfirmFirmwareModal: React.FC<ModalProps> = ({
  isOpen,
  setOpen,
  setOpenSingle=null,
  setOpenMultiple=null,
  setFirmware,
  setVersion,
  spas
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {data: availableFirmware, loading} = useSelector((state: RootState) => state.firmware);
  const [selectedFirmware, setSelectedFirmware] = useState<any>("");
  const [selectedVersion, setSelectedVersion] = useState<any>("");
  const { canAccess: canAccessDev } = usePermissionCheck(['view_dev_firmware']);
  const { canAccess: canAccessAdmin } = usePermissionCheck(['view_admin_firmware']);
  // Admin firmware is a subset of dev firmware, so the Admin tab is redundant for
  // users who can already see the Dev tab.
  const showAdminTab = canAccessAdmin && !canAccessDev;
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (activeTab === 0) {
      dispatch(fetchAvailableFirmware(""));
    } else if (canAccessDev) {
      dispatch(fetchAvailableDevFirmware(""));
    } else if (showAdminTab) {
      dispatch(fetchAvailableAdminFirmware(""));
    }
}, [dispatch, activeTab, canAccessDev, showAdminTab]);


  const handleReset = () => {
    setSelectedFirmware("");
    setSelectedVersion("");
    setActiveTab(0);
  };


  const handleClose = () => {
    handleReset();
    setOpen(false);
  };

  const handleTabChange = (newValue: number) => {
    setSelectedVersion("");
    setActiveTab(newValue);
  };

  const handleConfirm = () => {
    setFirmware(selectedFirmware);
    setVersion(selectedVersion);
    if (setOpenSingle) {
      setOpenSingle(true);
    } else if (setOpenMultiple) {
      setOpenMultiple(true);
    }
    handleClose();
  }

  const handleFirmware = (e:any) => {
    let val = e.target.value;
    setSelectedFirmware(val)
    if (val==='all') {
      setSelectedVersion('latest')
    } else {
      setSelectedVersion('')
    }
  }

  const versions = availableFirmware.filter((f:any)=>f.type===selectedFirmware);
  const noVersions = selectedFirmware!=="" && selectedFirmware!=="all" && versions.length===0 && !loading;

  return (
    <ModalShell
      open={isOpen}
      onClose={handleClose}
      title={spas.length>1 ? `Update firmware on ${spas.length} spas` : "Update firmware"}
      subtitle="Pick which firmware to install and the version to install."
      actions={
        <>
          <button className="btn" onClick={handleClose}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={spas.length===0 || selectedFirmware==="" || selectedVersion===""}
            onClick={handleConfirm}
          >
            Update firmware
          </button>
        </>
      }
    >
      {(canAccessDev || showAdminTab) && (
        <div className="segmented" role="tablist" aria-label="Firmware channel" style={{alignSelf: 'flex-start'}}>
          <button type="button" role="tab" aria-selected={activeTab===0} className={activeTab===0 ? 'active' : ''} onClick={() => handleTabChange(0)}>Public</button>
          <button type="button" role="tab" aria-selected={activeTab===1} className={activeTab===1 ? 'active' : ''} onClick={() => handleTabChange(1)}>
            {canAccessDev ? 'Dev' : 'Admin'}
          </button>
        </div>
      )}

      <ModalSpaList spas={spas} label={spas.length===1 ? 'Spa to update' : 'Spas to update'}/>

      <div className="dlg-row">
        <FormControl fullWidth size="small">
          <InputLabel id="firmware-selection-label">Firmware</InputLabel>
          <Select
            labelId="firmware-selection-label"
            label="Firmware"
            onChange={(e:any) =>handleFirmware(e)}
            value={selectedFirmware}
          >
            <MenuItem value={`all`}>All</MenuItem>
            <MenuItem value={`yocto`}>Yocto</MenuItem>
            <MenuItem value={`lpc`}>LPC</MenuItem>
            <MenuItem value={`spaboy`}>{palName}</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel id="version-selection-label" disabled={availableFirmware.length===0 || loading}>Version</InputLabel>
          <Select
            labelId="version-selection-label"
            label="Version"
            onChange={e => setSelectedVersion(e.target.value)}
            disabled={selectedFirmware==="" || availableFirmware.length===0 || loading}
            value={selectedVersion??""}
            endAdornment={loading ? <CircularProgress size={16} sx={{mr: 3}}/> : undefined}
          >
            {selectedFirmware!=='all' ?
              versions.map((f:any,i:number)=>
                <MenuItem key={i} value={f?.version}>{f?.version}</MenuItem>
              )
              :
              <MenuItem value={'latest'}>{'Latest'}</MenuItem>
            }
          </Select>
        </FormControl>
      </div>

      {noVersions && <div className="dlg-notice warning">No firmware found for this type.</div>}
    </ModalShell>
  );
};

export default ConfirmFirmwareModal;
